import styles from "./DefaultFormSelectField.module.scss";
import { Controller, useFormContext } from "react-hook-form";
import { AppDropDown } from "./abstract/AppDropDown";
import { classNames } from "primereact/utils";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { useSelectField } from "./hooks/useSelectField";
import { SkeletonItem } from "../../../skeletons/SkeletonItem";
import { useAppContext } from "../../../../../AppContext";
import { DropdownFilterEvent } from "primereact/dropdown";
import { AppTooltip } from "../../../tooltip/AppTooltip";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IDefaultFormSelectFieldProps<TOption> {
  name: string;
  options: TOption[] | undefined;
  tooltip?: string;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  filter?: boolean;
  editable?: boolean;
  width?: string;
  minWidth?: string;
  defaultValue?: string;
  label?: string;
  required?: boolean;
  includeId?: boolean;
  filterBy?: string;
  emptyMessage?: string;
  includeEmptyOption?: boolean;
  emptyOptionLabel?: string;
  optionDisabled?: string;
  onFilter?: (event: DropdownFilterEvent) => TOption[] | undefined;
  /**
   * Called when the USER picks an option — including re-picking the option the
   * field already holds (PrimeReact fires no onChange for that, so the click is
   * caught on the way down instead). Never called for programmatic
   * `setValue` writes, which is what lets callers tell a deliberate choice
   * apart from one the form made for them.
   */
  onUserSelect?: () => void;
}

export const DefaultFormSelectField = <TOption,>({
  name,
  options,
  tooltip,
  isLoading = false,
  disabled,
  className,
  filter = true,
  editable = false,
  width,
  minWidth,
  defaultValue,
  label,
  required,
  includeId,
  filterBy,
  emptyMessage,
  includeEmptyOption = false,
  emptyOptionLabel = "Без избор",
  optionDisabled,
  onFilter,
  onUserSelect,
}: IDefaultFormSelectFieldProps<TOption>) => {
  const { control } = useFormContext();
  const { isFocused, onFocus, onBlur } = useSelectField();
  const { isDesktopOrTablet } = useAppContext();

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  if (!options) {
    <></>;
  }
  const normalizedOptions = options ?? [];

  const computedOptions = includeEmptyOption
    ? [{ name: emptyOptionLabel, id: null }, ...normalizedOptions]
    : options;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div
          style={{ width: width, minWidth: minWidth }}
          className={classNames(styles.root, className, {
            [styles.root_disabled]: disabled,
          })}
          onClickCapture={
            onUserSelect
              ? (event) => {
                  // The overlay panel is a React portal, so option clicks still
                  // travel through this element. PrimeReact swallows a click on
                  // the ALREADY selected option (no onChange at all), so this is
                  // the only place a deliberate re-pick is observable.
                  const option = (
                    event.target as HTMLElement | null
                  )?.closest?.('li[role="option"]');

                  if (option) {
                    onUserSelect();
                  }
                }
              : undefined
          }
          onKeyDownCapture={
            onUserSelect
              ? (event) => {
                  // Keyboard equivalent of the click above: Enter commits the
                  // focused option, again with no onChange when it is the one
                  // already selected.
                  if (event.key === "Enter") {
                    onUserSelect();
                  }
                }
              : undefined
          }
        >
          <RenderWhen condition={!!label}>
            <div
              className={classNames(styles.root_label, {
                [styles.root_label_interacted_with]: true,
                [styles.root_label_error]: !!fieldState.error?.message,
              })}
            >
              <p>
                {label}
                {required ? "*" : ""}
              </p>
              <AppTooltip content={tooltip} />
            </div>
          </RenderWhen>

          <SkeletonItem
            className={styles.root_dropdown_skeleton}
            isLoading={isLoading}
            value={
              <AppDropDown
                field={field}
                onChange={
                  onUserSelect
                    ? (value) => {
                        field.onChange(value);
                        onUserSelect();
                      }
                    : undefined
                }
                options={computedOptions}
                disabled={disabled}
                filter={filter}
                editable={editable}
                className={styles.root_dropdown}
                width={width}
                invalid={fieldState.error != null}
                defaultValue={defaultValue}
                onFocus={onFocus}
                onBlur={onBlur}
                includeId={includeId}
                onFilter={onFilter}
                emptyMessage={emptyMessage}
                filterBy={filterBy}
                optionDisabled={optionDisabled}
              />
            }
          />
        </div>
      )}
    />
  );
};
